/* eslint-disable no-restricted-syntax -- this line was auto generated, hence fix the issue timely */
import React from "react";
import { startCase, lowerCase } from "lodash";
import { useTheme, Text, Icon } from "@bookingcom/bui-react";
import { useI18n, I18nChildContext } from "@bookingcom/lingojs-react";
import { t } from "@bookingcom/lingojs-core";
import { BagPersonalItemIcon, CabinTrolleyIcon, BagHoldIcon } from "@bookingcom/bui-assets-react/streamline/index.js";

import { UIBrandedFareInfo, UIFlightSegment } from "@flights/types";
import { getLeastAvailableLuggagePerTrip } from "../../../../store/selectors/searchResultsAvailableLuggage";
import useUserAgent from "../../../../hooks/useUserAgent";
import useGlobalContext from "../../../../hooks/useGlobalContext";
import useLocaleContext from "hooks/useLocaleContext";
import Frame from "components/elements/Frame";

type FlightCardLuggageProps = {
  segments: UIFlightSegment[];
  horizontal?: boolean;
  brandedFareInfo?: UIBrandedFareInfo;
  isAlternativeFareCard?: boolean;
};

const FlightCardLuggage = (props: FlightCardLuggageProps) => {
  const { segments, horizontal = false, brandedFareInfo, isAlternativeFareCard = false } = props;
  const { isMobile: _isMobile } = useUserAgent();
  const { supportsChildrenTickets } = useGlobalContext();
  const i18n = useI18n() as I18nChildContext;

  const isMobile = isAlternativeFareCard || _isMobile;

  if (!segments.length) {
    return null;
  }
  const { cabinLuggage, personalItem, checkedInLuggage } = getLeastAvailableLuggagePerTrip(
    segments,
    supportsChildrenTickets
  );

  const availableBaggageList: string[] = [];

  if (personalItem) {
    availableBaggageList.push(i18n.trans(t("flights_baggage_personal_item")));
  }

  if (cabinLuggage) {
    availableBaggageList.push(i18n.trans(t("flights_baggage_cabin_bag")));
  }

  if (checkedInLuggage) {
    availableBaggageList.push(i18n.trans(t("flights_baggage_checked_bag")));
  }

  if (!availableBaggageList.length) {
    return null;
  }

  return (
    <div>
      <Frame
        direction={horizontal ? "row" : "column"}
        alignItems={horizontal ? "center" : undefined}
        mt={horizontal ? 4 : 0}
      >
        <Frame
          grow={horizontal ? 0 : 3}
          direction="row"
          alignItems="flex-end"
          justifyContent={isMobile || horizontal ? "flex-start" : "flex-end"}
          pb={isMobile || horizontal ? 0 : 2}
          mr={horizontal ? 2 : 0}
        >
          {personalItem && <Icon svg={BagPersonalItemIcon} size={horizontal ? "medium" : "large"} />}
          {cabinLuggage && <Icon svg={CabinTrolleyIcon} size={horizontal ? "medium" : "large"} />}
          {checkedInLuggage && <Icon svg={BagHoldIcon} size={horizontal ? "medium" : "large"} />}
        </Frame>
        <LuggageDescription
          horizontal={horizontal}
          brandedFareInfo={brandedFareInfo}
          availableBaggageList={availableBaggageList}
          isAlternativeFareCard={isAlternativeFareCard}
        />
      </Frame>
    </div>
  );
};

type LuggageDescriptionProps = {
  availableBaggageList: string[];
  horizontal?: boolean;
  brandedFareInfo?: UIBrandedFareInfo;
  isAlternativeFareCard?: boolean;
};

const LuggageDescription = (props: LuggageDescriptionProps) => {
  const { availableBaggageList, horizontal, brandedFareInfo, isAlternativeFareCard = false } = props;
  const { isRTL } = useLocaleContext();
  const { isMobile: _isMobile } = useUserAgent();
  const i18n = useI18n();
  const theme = useTheme();

  const isMobile = isAlternativeFareCard || _isMobile;

  let style: React.CSSProperties = {
    marginBottom: horizontal ? 0 : theme.units.spacing_4x
  };

  if (isMobile) {
    style = {
      marginBottom: 0,
      marginRight: isRTL ? 0 : theme.units.spacing_4x,
      marginLeft: isRTL ? theme.units.spacing_4x : 0
    };
  }

  const getBaggageInfoTags = () => {
    switch (availableBaggageList.length) {
      case 1:
        return <span>{availableBaggageList[0]}</span>;

      case 2:
        return (
          <span>
            {i18n.trans(
              t("flights_baggage_included_placeholder_combo_1", {
                variables: {
                  baggage_1: availableBaggageList[0],
                  baggage_2: availableBaggageList[1]
                }
              })
            )}
          </span>
        );

      case 3:
        return (
          <span>
            {i18n.trans(
              t("flights_baggage_included_placeholder_combo_2", {
                variables: {
                  baggage_1: availableBaggageList[0],
                  baggage_2: availableBaggageList[1],
                  baggage_3: availableBaggageList[2]
                }
              })
            )}
          </span>
        );
      default:
        return;
    }
  };

  return (
    <div style={style}>
      {availableBaggageList.length > 0 && (
        <div
          style={{
            display: "block",
            color: theme.colors.color_foreground_alt,
            textAlign: isMobile || horizontal ? "start" : "end"
          }}
        >
          <Text variant={horizontal ? "small_1" : "small_2"}>
            {!!brandedFareInfo ? (
              <span>
                {`${i18n.trans(
                  t("flights_branded_fare_baggage_included_sr", {
                    variables: {
                      branded_fare: `${startCase(lowerCase(brandedFareInfo?.fareName))}`
                    }
                  })
                )} `}
              </span>
            ) : (
              <span>{`${i18n.trans(t("flights_baggage_included"))} `}</span>
            )}
            <span>{getBaggageInfoTags()}</span>
          </Text>
        </div>
      )}
    </div>
  );
};

export default FlightCardLuggage;
